import useSWR from 'swr'
import useSWRInfinite from 'swr/infinite'
import invariant from 'tiny-invariant'

import { Dinner } from '~/api-definition/definition'
import { DinnerMemberType } from '~/api-definition/enumTypes'
import {
  DinnerParticipationResponse,
  DinnerResponse,
  DinnersResponse,
  SuccessResponse,
} from '~/api-definition/response'
import { DinnerInvitationCodeResponse } from '~/api-definition/response/dinnerInvitationCodeResponse'
import { config } from '~/config'
import { optLast } from '~/libs/array'
import { formatDateTime } from '~/libs/date'
import { dinnerRestaurantName } from '~/libs/dinner'
import { useSession } from '~/provider/SessionProvider'

import { WithPaging, useFoodSWR } from '.'
import { superFetch } from '..'

export const dinnerGetDinners = (args?: {
  searchParams?: WithPaging<{
    member_filter?: 'organized' | 'attended'
    sort_by?: 'scheduled_at_desc' | 'created_at_desc'
    only_seats_left?: boolean
    start_date?: string
    end_date?: string
  }>
  accessToken?: string
}) => {
  const headers: Record<string, string> = {}
  if (args?.accessToken) {
    headers['Authorization'] = `Bearer ${args.accessToken}`
  }
  return superFetch<DinnersResponse>(
    config.api.url('/dinner/dinners', { searchParams: args?.searchParams }),
    { method: 'GET', headers }
  )
}

export const dinnerGetDinnerParticipation = (args: {
  accessToken: string
  searchParams: { dinner_id: string }
}) => {
  const headers: Record<string, string> = {}
  headers['Authorization'] = `Bearer ${args.accessToken}`
  return superFetch<DinnerParticipationResponse>(
    config.api.url('/dinner/dinner_participation', {
      searchParams: args.searchParams,
    }),
    { method: 'GET', headers }
  )
}

export const dinnerGetDinner = (args: {
  accessToken?: string
  searchParams: { dinner_id: string } | { dinner_invitation_code: string }
}) => {
  const headers: Record<string, string> = {}
  if (args.accessToken) {
    headers['Authorization'] = `Bearer ${args.accessToken}`
  }
  return superFetch<DinnerResponse>(
    config.api.url('/dinner/dinner', {
      searchParams: args.searchParams,
    }),
    { method: 'GET', headers }
  )
}

// TODO: https://github.com/W3-Fortune/food_web/issues/57
export const dinnerNewDinner = (args: {
  accessToken: string
  body: {
    dinner_name: string
    scheduled_at: number
    approvement_scheduled_at?: number
    reservation_number: number
    recruitment_number: number
    privacy_type: string
    min_budget_amount?: number
    max_budget_amount?: number
    thumbnail_image_ids?: string[]
    restaurant_name?: string
    restaurant_address?: string
    restaurant_url?: string
    favorite_alcohol_type: string
    alcohol_amount_type: string
    food_amount_type: string
    sex_type: string
    payment_type: string
    invited_candidates?: {
      member_type: string
      user_id: string
    }[]
    min_age?: number
    max_age?: number
    note?: string
  }
}) => {
  const headers: Record<string, string> = {}
  if (args.accessToken) {
    headers['Authorization'] = `Bearer ${args.accessToken}`
  }
  return superFetch<DinnerResponse>(config.api.url('/dinner/new_dinner'), {
    method: 'POST',
    contentType: 'application/x-www-form-urlencoded',
    headers,
    body: args.body,
  })
}

export const dinnerUpdateDinner = (args: {
  accessToken: string
  body: {
    dinner_id: string
    dinner_name?: string
    scheduled_at?: number
    approvement_scheduled_at?: number
    reservation_number?: number
    recruitment_number?: number
    privacy_type?: string
    min_budget_amount?: number
    max_budget_amount?: number
    fixed_budget_amount?: number
    thumbnail_image_ids?: string[]
    restaurant_id?: string
    restaurant_name?: string
    restaurant_address?: string
    restaurant_url?: string
    favorite_alcohol_type?: string
    alcohol_amount_type?: string
    food_amount_type?: string
    sex_type?: string
    payment_type?: string
    min_age?: number
    max_age?: number
    note?: string
    memo_text?: string
  }
}) => {
  const headers: Record<string, string> = {}
  headers['Authorization'] = `Bearer ${args.accessToken}`
  return superFetch<DinnerResponse>(config.api.url('/dinner/update_dinner'), {
    method: 'PUT',
    contentType: 'application/x-www-form-urlencoded',
    headers,
    body: args.body,
  })
}

export const dinnerApplyDinner = (args: {
  accessToken: string
  body: {
    dinner_id: string
  }
}) => {
  const headers: Record<string, string> = {}
  headers['Authorization'] = `Bearer ${args.accessToken}`
  return superFetch<SuccessResponse>(config.api.url('/dinner/apply_dinner'), {
    method: 'POST',
    contentType: 'application/x-www-form-urlencoded',
    headers,
    body: args.body,
  })
}

export const dinnerApproveDinnerInvitation = (args: {
  accessToken: string
  body: {
    dinner_id: string
  }
}) => {
  const headers: Record<string, string> = {}
  headers['Authorization'] = `Bearer ${args.accessToken}`
  return superFetch<SuccessResponse>(
    config.api.url('/dinner/approve_dinner_invitation'),
    {
      method: 'PUT',
      contentType: 'application/x-www-form-urlencoded',
      headers,
      body: args.body,
    }
  )
}

export const dinnerCancelDinner = (args: {
  accessToken: string
  searchParams: {
    dinner_id: string
  }
}) => {
  const headers: Record<string, string> = {}
  headers['Authorization'] = `Bearer ${args.accessToken}`
  return superFetch<SuccessResponse>(
    config.api.url('/dinner/cancel_dinner', {
      searchParams: args.searchParams,
    }),
    {
      method: 'DELETE',
      headers,
    }
  )
}

export const dinnerGetInvitationCode = (args: {
  accessToken: string
  searchParams: {
    dinner_id: string
  }
}) => {
  const headers: Record<string, string> = {
    Authorization: `Bearer ${args.accessToken}`,
  }
  return superFetch<DinnerInvitationCodeResponse>(
    config.api.url('/dinner/invitation_code', {
      searchParams: args.searchParams,
    }),
    {
      method: 'GET',
      headers,
    }
  )
}

export const dinnerRenewInvitationCode = (args: {
  accessToken: string
  body: {
    dinner_id: string
  }
}) => {
  const headers: Record<string, string> = {
    Authorization: `Bearer ${args.accessToken}`,
  }
  return superFetch<DinnerInvitationCodeResponse>(
    config.api.url('/dinner/renew_invitation_code'),
    {
      method: 'POST',
      contentType: 'application/x-www-form-urlencoded',
      headers,
      body: args.body,
    }
  )
}

// SWR
export const useDinnerDinners = (args: {
  searchParams: WithPaging<{
    member_filter?: 'organized' | 'attended'
    sort_by?: 'scheduled_at_desc' | 'created_at_desc'
    only_seats_left?: boolean
    start_date?: string
    end_date?: string
  }>
}) => {
  const pageSize = config.pageSize.dinnerList
  const session = useSession()

  return useSWRInfinite(
    (_, previousPageData: DinnersResponse | undefined) => {
      const searchParams = {
        ...args.searchParams,
        cursor_next: optLast(previousPageData?.dinners)?.dinnerId,
        count: pageSize,
      }

      return [
        config.api.url('/dinner/dinners', {
          searchParams,
        }),
        searchParams,
        { withAuth: session.response?.data?.accessToken != null },
      ]
    },
    ([_, searchParams]) => {
      console.log(searchParams)
      return dinnerGetDinners({
        searchParams,
        accessToken: session.response?.data?.accessToken,
      })
    }
  )
}

export const useDinnerParticipation = (
  args:
    | {
        searchParams: { dinner_id: string }
      }
    | undefined
) => {
  const session = useSession()

  return useSWR(
    session.response?.data?.accessToken != null && args?.searchParams != null
      ? config.api.url('/dinner/dinner_participation', {
          searchParams: args.searchParams,
        })
      : null,
    () => {
      invariant(session.response?.data?.accessToken != null)
      invariant(args != null)
      return dinnerGetDinnerParticipation({
        searchParams: args.searchParams,
        accessToken: session.response?.data?.accessToken,
      })
    }
  )
}

export const useDinnerInvitationText = (args: {
  dinner: Dinner | undefined
  myMemberType: DinnerMemberType | undefined
}) => {
  const dinnerInvitation = useFoodSWR(
    '/dinner/invitation_code',
    {
      dinnerId: args.dinner?.dinnerId ?? '',
    },
    {
      disabled:
        args.dinner == null ||
        !(
          args.myMemberType === 'organizer' ||
          args.myMemberType === 'co_organizer'
        ),
    }
  )

  const dinnerInvitationUrl =
    dinnerInvitation.data == null
      ? null
      : config.invitationUrl(
          'dinner',
          dinnerInvitation.data.invitationCode.code ?? ''
        )

  const segments: string[] = []
  if (args.dinner != null) {
    segments.push(
      formatDateTime(
        { sec: args.dinner.scheduledAt },
        { formateDateOpts: { showDayOfWeek: true } }
      )
    )
    const restaurantName = dinnerRestaurantName(args.dinner)
    if (restaurantName != null && restaurantName !== '') {
      segments.push(restaurantName)
    }
    const dinnerName = args.dinner.dinnerName
    if (dinnerName != null && dinnerName !== '') {
      segments.push(dinnerName)
    }
    if (dinnerInvitationUrl != null) {
      segments.push(dinnerInvitationUrl)
    }
  }

  return {
    dinnerInvitationText: segments.join('\n'),
    dinnerInvitationUrl,
    error: dinnerInvitation.error,
  }
}
